/**
 * ForceSkillEffect — Grit grapple-path apotheosis: telekinetic mode cycling.
 *
 * Three modes cycle on each activation: Push (cone knockback, no damage),
 * Pull (drag nearest enemy 2 tiles toward player, no damage), Slam (lift
 * then smash — damage + stun). Mode state persists across cooldowns.
 *
 * All three modes share one very_long cooldown.
 *
 * Implements SkillEffect directly with internal mode and cooldown state.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Force
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ForceMode = 'push' | 'pull' | 'slam';

const MODE_SEQUENCE: readonly ForceMode[] = ['push', 'pull', 'slam'];

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ForceDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly statusEffects: {
    apply(statusId: string, ...args: unknown[]): void;
  };
  readonly target: {
    hasTargetInRange(): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ForceConfig {
  readonly cooldownMs: number;
  readonly pullDistanceTiles: number;
  readonly slamStunMs: number;
  readonly slamDamage: number;
}

const DEFAULT_CONFIG: ForceConfig = {
  cooldownMs: 4000,                // very_long cooldown tier
  pullDistanceTiles: 2,
  slamStunMs: 1000,
  slamDamage: 30,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ForceSkillEffect implements SkillEffect {
  readonly skillId = 'force';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: ForceDeps;
  private readonly config: ForceConfig;

  private _modeIndex = 0;
  private _cooldownRemaining = 0;
  private _lastModeFired: ForceMode | null = null;

  // Per-activation state
  private _pushFired = false;
  private _pullFired = false;
  private _slamFired = false;
  private _activationCount = 0;

  constructor(deps: ForceDeps, config: Partial<ForceConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get currentMode(): ForceMode { return MODE_SEQUENCE[this._modeIndex]; }
  get lastModeFired(): ForceMode | null { return this._lastModeFired; }
  get pushFired(): boolean { return this._pushFired; }
  get pullFired(): boolean { return this._pullFired; }
  get slamFired(): boolean { return this._slamFired; }
  get activationCount(): number { return this._activationCount; }

  get cooldown(): number { return this._cooldownRemaining; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const mode = MODE_SEQUENCE[this._modeIndex];
    this.fireMode(mode);
    this._lastModeFired = mode;
    this._activationCount++;

    // Advance to next mode (wraps around)
    this._modeIndex = (this._modeIndex + 1) % MODE_SEQUENCE.length;

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Force is instant — no toggle
  }

  isActive(): boolean {
    return false; // instant skill, never "active"
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }

  // --- Internal ---

  private fireMode(mode: ForceMode): void {
    switch (mode) {
      case 'push':
        this.firePush();
        break;
      case 'pull':
        this.firePull();
        break;
      case 'slam':
        this.fireSlam();
        break;
    }
  }

  private firePush(): void {
    // Cone knockback — no damage
    this.deps.statusEffects.apply(
      'force_push',
      0,
      { knockback: true },
      'force',
    );
    this._pushFired = true;
  }

  private firePull(): void {
    // Drag nearest enemy toward player — no damage
    this.deps.statusEffects.apply(
      'force_pull',
      0,
      { distanceTiles: this.config.pullDistanceTiles },
      'force',
    );
    this._pullFired = true;
  }

  private fireSlam(): void {
    // Lift then smash — damage + stun
    this.deps.statusEffects.apply(
      'force_slam',
      0,
      { damage: this.config.slamDamage },
      'force',
    );
    // Apply stun
    this.deps.statusEffects.apply(
      'stun',
      this.config.slamStunMs,
      'force',
    );
    this._slamFired = true;
  }
}
